import React, { useEffect, useState, useCallback } from "react";
import { Download, Smartphone, X, Share2, Plus } from "lucide-react";

const DISMISS_KEY = "ethos_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasRecentlyDismissed() {
  // Nota sicurezza: localStorage qui contiene SOLO il timestamp del dismiss
  // del banner di installazione PWA. Nessun dato sensibile (token, PII, credenziali).
  // Il code-quality tool segnala l'uso di localStorage in generale ma questo caso è sicuro.
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const iosDevice = isIOS();

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS has no beforeinstallprompt — show manual instructions after 4s
    let iosTimer;
    if (iosDevice) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [iosDevice]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
      setInstalling(false);
    }
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl md:left-auto md:right-6 md:bottom-6"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-700 text-white">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
            Ethos Costruzioni
          </div>
          <div className="mt-0.5 text-sm font-semibold text-slate-900">
            Installa l'app sullo smartphone
          </div>

          {iosDevice ? (
            <div className="mt-2 text-xs leading-relaxed text-slate-600">
              Per aggiungerla alla schermata Home su iPhone:
              <ol className="mt-1 list-decimal space-y-0.5 pl-4">
                <li>
                  Tocca <Share2 className="inline h-3.5 w-3.5" /> <b>Condividi</b> nella barra di Safari
                </li>
                <li>
                  Scorri e scegli <Plus className="inline h-3.5 w-3.5" /> <b>Aggiungi alla schermata Home</b>
                </li>
                <li>Conferma toccando <b>Aggiungi</b></li>
              </ol>
            </div>
          ) : (
            <div className="mt-1 text-xs text-slate-600">
              Accedi rapidamente a timbrature, rapportini e bolle direttamente dalla schermata principale del tuo telefono.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            {!iosDevice && (
              <button
                onClick={install}
                disabled={installing}
                data-testid="pwa-install-button"
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
              >
                <Download className="h-3.5 w-3.5" />
                {installing ? "Installazione…" : "Installa app"}
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="pwa-dismiss-button"
              className="rounded-md border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Più tardi
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="pwa-close-button"
          aria-label="Chiudi"
          className="ml-1 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
